import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import { CommunityInvite, CommunityInvitePropertyType } from '../../../models/inviteService.model';

interface DisableMemberAdministrationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentInvite?: CommunityInvite;
  onConfirmDisable: () => void;
}

export default function DisableMemberAdministrationModal({
  open,
  setOpen,
  currentInvite,
  onConfirmDisable,
}: DisableMemberAdministrationModalProps): JSX.Element {
  const { t } = useTranslation();

  const getFieldDisplayName = (field: CommunityInvitePropertyType): string => {
    switch (field) {
      case CommunityInvitePropertyType.EMAIL:
        return t('dataType.email');
      case CommunityInvitePropertyType.PHONENUMBER:
        return t('dataType.phoneNumber');
      case CommunityInvitePropertyType.ADDRESS:
        return t('dataType.address');
      case CommunityInvitePropertyType.BIRTHDATE:
        return t('dataType.birthDate');
      case CommunityInvitePropertyType.BUSINESSNAME:
        return t('dataType.businessName');
      default:
        return field;
    }
  };

  const enabledDataRequests = currentInvite 
    ? [...currentInvite.mandatoryFields, ...currentInvite.optionalFields]
    : [];

  const customFieldsCount = currentInvite?.customFields?.length || 0;

  const handleConfirm = () => {
    onConfirmDisable();
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      className="relative transform overflow-hidden rounded-[12px] bg-secondary-50 px-6 py-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-primary-900 mb-4">
          {t('component.modal.disableMemberAdministration.title')}
        </h3>
        
        <div className="mb-6">
          <p className="text-sm text-primary-700 mb-3">
            {t('component.modal.disableMemberAdministration.message')}
          </p>
          
          {enabledDataRequests.length > 0 && (
            <div className="bg-secondary-100 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-primary-900 mb-2">
                {t('component.modal.disableMemberAdministration.basicFields')}
              </p>
              <ul className="text-sm text-primary-800 space-y-1">
                {enabledDataRequests.map((field) => (
                  <li key={field} className="flex items-center">
                    <span className="w-2 h-2 bg-primary-600 rounded-full mr-2" />
                    {getFieldDisplayName(field)}
                    {currentInvite?.mandatoryFields.includes(field) && (
                      <span className="ml-2 text-xs text-primary-500">
                        ({t('form.invite.mandatory')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {customFieldsCount > 0 && (
            <div className="bg-secondary-100 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-primary-900 mb-1">
                {t('component.modal.disableMemberAdministration.customFields')}
              </p>
              <p className="text-sm text-primary-800">
                {t('component.modal.disableMemberAdministration.customFieldsCount', { count: customFieldsCount })}
              </p>
            </div>
          )}
          
          <p className="text-sm text-primary-700 mt-3">
            {t('component.modal.disableMemberAdministration.warning')}
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="px-4 py-2"
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700"
          >
            {t('general.continue')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}